// ©2024 Austin App House. All rights reserved.

/** Visually hide react-select combobox <input> (keeps focus/a11y, no visible ghost field). */
export const ghostSelectInput = (styles) => ({
  ...styles,
  color: 'transparent',
  opacity: 0,
  width: 1,
  minWidth: 0,
  maxWidth: 1,
  height: 1,
  position: 'absolute',
  pointerEvents: 'none',
  margin: 0,
  padding: 0,
  border: 0,
  outline: 0,
  background: 'transparent',
  gridArea: 'unset',
});

/** Visible white input for searchable dashboard selects. */
export const dashboardSearchableInput = (styles) => ({
  ...styles,
  color: '#ffffff',
  opacity: 1,
  margin: 0,
  padding: 0,
  border: 0,
  outline: 0,
  background: 'transparent',
  boxShadow: 'none',
  pointerEvents: 'auto',
  width: '100%',
  minWidth: 2,
  caretColor: '#ffffff',
  textAlign: 'left',
  gridArea: 'unset',
});

/** Dark-theme chip styles for react-select isMulti tags */
export const dashboardMultiValueStyles = {
  multiValue: (base) => ({
    ...base,
    backgroundColor: 'rgba(0, 64, 220, 0.2)',
    border: '1px solid rgba(0, 102, 255, 0.35)',
    borderRadius: 999,
    margin: 0,
    alignSelf: 'center',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 500,
    padding: '2px 8px',
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: 'rgba(255, 255, 255, 0.55)',
    borderRadius: '0 999px 999px 0',
    padding: '0 6px',
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
      color: '#ffffff',
    },
  }),
};

export const white = {
  control: provided => ({
    ...provided,
    color: 'black',
    borderRadius: 'none',
  }),
  menu: (provided) => ({
    ...provided,
    color: 'black',
    borderRadius: 'none',
    marginTop: 0,
    marginBottom: 0,
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    color: 'black',
  }),
  container: (base) => ({
    ...base,
    width: '100%',
  })
};

export const blueDark = {
  singleValue: styles => ({
    ...styles,
    color: 'white',
  }),
  control: styles => ({
    ...styles,
    backgroundColor: '#1e2f41',
    borderColor: '#1e2f41',
  }),
  option: (styles, {data, isDisabled, isFocused, isSelected}) => {
    return {
      ...styles,
      fontWeight: isFocused ? 'bold' : 'normal',
      backgroundColor: isSelected ? '#e6e3e3' : 'white',
      color: '#000000',
    };
  },
};

export const dark = {
  input: ghostSelectInput,
  placeholder: styles => ({
    ...styles,
    color: 'white',
  }),
  singleValue: styles => ({
    ...styles,
    color: 'white',
  }),
  control: styles => ({
    ...styles,
    backgroundColor: '#182837',
    borderColor: '#182837',
    borderRadius: 'none',
  }),
  option: (styles, {data, isDisabled, isFocused, isSelected}) => {
    return {
      ...styles,
      fontWeight: isFocused ? 'bold' : 'normal',
      color: '#000000',
    };
  },
  ...dashboardMultiValueStyles,
};

/** React-select styles matching dashboard-input (Next) */
export const dashboard = {
  container: (base) => ({
    ...base,
    width: '100%',
    fontFamily: 'var(--font-body, "Lexend", sans-serif)',
  }),
  control: (base, state) => ({
    ...base,
    minHeight: 48,
    alignItems: 'center',
    backgroundColor: '#000000',
    borderColor: state.isFocused ? 'var(--cobalt-bright, #2a5cff)' : 'rgba(255, 255, 255, 0.1)',
    borderWidth: 1,
    borderRadius: 999,
    boxShadow: 'none',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#000000',
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '6px 12px',
    color: '#ffffff',
    gap: 4,
    alignItems: 'center',
    alignContent: 'center',
    justifyContent: 'flex-start',
  }),
  input: ghostSelectInput,
  placeholder: (base) => ({
    ...base,
    color: 'rgba(255, 255, 255, 0.4)',
    margin: 0,
    textAlign: 'left',
  }),
  singleValue: (base) => ({
    ...base,
    color: '#ffffff',
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#050d24',
    border: '1px solid rgba(0, 102, 255, 0.2)',
    borderRadius: 12,
    overflow: 'hidden',
    zIndex: 200,
    marginTop: 0,
    marginBottom: 0,
  }),
  menuList: (base) => ({
    ...base,
    padding: 6,
  }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    borderRadius: 8,
    backgroundColor: isSelected
      ? 'var(--cobalt, #0040dc)'
      : isFocused
        ? 'rgba(0, 64, 220, 0.2)'
        : 'transparent',
    color: '#ffffff',
    cursor: 'pointer',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({
    ...base,
    color: 'rgba(255, 255, 255, 0.5)',
    paddingRight: 12,
    '&:hover': { color: '#ffffff' },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: 'rgba(255, 255, 255, 0.5)',
    '&:hover': { color: '#ffffff' },
  }),
  ...dashboardMultiValueStyles,
};

/** classNames for react-select inside dashboard modals */
export const dashboardSelectClassNames = {
  control: ({ isFocused }) => (
    `dashboard-select-control${isFocused ? ' dashboard-select-control--is-focused' : ''}`
  ),
  menu: () => 'dashboard-select-menu',
  menuList: () => 'dashboard-select-menu-list',
  option: ({ isFocused, isSelected }) => (
    `dashboard-select-option${isFocused ? ' dashboard-select-option--focused' : ''}${isSelected ? ' dashboard-select-option--selected' : ''}`
  ),
  singleValue: () => 'dashboard-select-single-value',
  placeholder: () => 'dashboard-select-placeholder',
  valueContainer: () => 'dashboard-select-value-container',
  input: () => 'dashboard-select-input',
  multiValue: () => 'dashboard-select-multi-value',
  multiValueLabel: () => 'dashboard-select-multi-value-label',
  multiValueRemove: () => 'dashboard-select-multi-value-remove',
};

/** Dashboard styles for searchable selects — white visible typed text. */
export const dashboardSearchable = {
  ...dashboard,
  input: dashboardSearchableInput,
};

/** Pick dashboard select styles based on whether the field is searchable. */
export const getDashboardSelectStyles = (isSearchable = false) => (
  isSearchable ? dashboardSearchable : dashboard
);

export default ({
  white,
  dark,
  blueDark,
  dashboard,
})
